/**
 * Please note: this is a copy of the existing member portal theme (as of 2022-10-22).
 * In general, it had more examples of things we could do and was chosen for this reason.
 */

import { Theme, Color } from '../'

const tiaColors: Record<
    string,
    Color | undefined | ((t: DefaultTheme) => Color | undefined)
> = {
    /* non-semantic colors for convenience */
    softBlack: '#282725' as Color,
    white: '#FFFFFF' as Color,
    poppy: '#F95647' as Color,

    raspberry: '#831A4A' as Color,
    cream: '#FCF4E9' as Color,
    oat: '#ECDCCD' as Color,
    clay: '#BE9191' as Color,
    putty: '#765454' as Color,
    terraCotta: '#F5907A' as Color,
    brick: '#C45138' as Color,
    pistachio: '#D6DEBA' as Color,
    gold: '#F9B146' as Color,
    tiaPink: '#EF308D' as Color,

    /* semantic colors for actual use */
    mainBackground: (t: DefaultTheme) => t.colors.cream,
    secondaryBackground: (t: DefaultTheme) => t.colors.oat,
    navBackground: (t: DefaultTheme) => t.colors.softBlack,
    primaryButton: (t: DefaultTheme) => t.colors.raspberry,
    darkSecondaryButton: (t: DefaultTheme) => t.colors.softBlack,
    lightSecondaryButton: (t: DefaultTheme) => t.colors.white,
    onDemandCareButton: (t: DefaultTheme) => t.colors.gold,
    inactiveButton: (t: DefaultTheme) => t.colors.oat,

    text: (t: DefaultTheme) => t.colors.softBlack,
    inactiveText: (t: DefaultTheme) => t.colors.clay,
    supportText: (t: DefaultTheme) => t.colors.putty,
    linkText: (t: DefaultTheme) => t.colors.raspberry,

    inputBackground: (t: DefaultTheme) => t.colors.white,
    inputBackgroundInactive: (t: DefaultTheme) => t.colors.oat,
    inputBackgroundSelected: (t: DefaultTheme) => t.colors.oat,
    inputOutline: (t: DefaultTheme) => t.colors.oat,
    inputOutlineHover: (t: DefaultTheme) => t.colors.clay,
    inputOutlineSelected: (t: DefaultTheme) => t.colors.putty,
    inputOutlineRequired: (t: DefaultTheme) => t.colors.pistachio,
    inputOutlineError: (t: DefaultTheme) => t.colors.poppy,

    cardOutline: (t: DefaultTheme) => t.colors.oat,
    cardBackground: (t: DefaultTheme) => t.colors.white,

    darkIconDefault: (t: DefaultTheme) => t.colors.clay,
    darkIconHover: (t: DefaultTheme) => t.colors.softBlack,
    darkIconInactive: (t: DefaultTheme) => t.colors.oat,
    lightIconDefault: (t: DefaultTheme) => t.colors.white,
    lightIconHover: (t: DefaultTheme) => t.colors.oat,
    lightIconInactive: 'rgba(255, 255, 255, 0.2)' as Color,
    communicationBanner: (t: DefaultTheme) => t.colors.tiaPink,
    alertBanner: (t: DefaultTheme) => t.colors.poppy,

    lightOverlay: 'rgba(255, 255, 255, 0.2)' as Color,
    darkOverlay: 'rgba(0, 0, 0, 0.2)' as Color,
    modalOverlay: 'rgba(118, 84, 84, 0.2)' as Color
}

const headings = {
    h1: {
        color: 'text',
        fontFamily: 'headingSerif',
        lineHeight: '36px',
        fontWeight: 'headingSerif',
        fontSize: 8,
        mb: 5
    },
    h2: {
        color: 'text',
        fontFamily: 'headingSansSerif',
        lineHeight: '28px',
        fontWeight: 'headingSansSerif',
        fontSize: 7,
        mb: 4
    },
    h3: {
        color: 'text',
        fontFamily: 'headingSerif',
        lineHeight: '24px',
        fontWeight: 'headingSerif',
        fontSize: 6,
        mb: 4
    },
    h4: {
        color: 'text',
        fontFamily: 'headingSansSerif',
        lineHeight: '24px',
        fontWeight: 'bold',
        fontSize: 5,
        mb: 3
    },
    cardTitle: {
        color: 'text',
        fontFamily: 'headingSansSerif',
        lineHeight: 'headingSansSerif',
        fontSize: 5,
        mb: 4
    },
    bigTitle: {
        color: 'text',
        fontFamily: 'headingSansSerif',
        lineHeight: 'headingSansSerif',
        fontWeight: 'headingSansSerif',
        mb: 5,
        fontSize: [8, 9]
    }
}

const tiaInput = {
    'borderColor': 'inputOutline',
    'px': 4,
    'py': 3,
    'height': 7,
    'color': 'text',
    'fontSize': 4,
    'fontFamily': 'body',
    'mb': 4,
    'borderRadius': 2,
    'bg': 'inputBackground',
    '&:focus': {
        outline: 'none',
        borderColor: 'inputOutlineSelected',
        bg: 'inputBackground'
    },
    '&:hover': {
        borderColor: 'inputOutlineHover'
    },
    '&::placeholder': {
        color: 'placeholder'
    },
    '&:disabled': {
        'backgroundColor': 'inputBackgroundInactive',
        'borderColor': 'inputBackgroundInactive',
        '&::placeholder': {
            color: 'inactiveText'
        }
    },
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
        boxShadow: 'inset 0 0 0 1000px white'
    }
}

const tiaButton = {
    'cursor': 'pointer',
    'borderRadius': 2,
    'width': ['100%', 'auto'],
    'maxWidth': 480,
    'variant': 'text.button',
    'py': 3,
    'px': [0, 6],
    '&:hover': {
        outline: 'none',
        filter: `brightness(120%)`
    },
    '&:active': {
        outline: 'none',
        filter: 'brightness(80%)'
    }
}

export const defaultTheme = {
    space: [0, 4, 8, 12, 16, 24, 32, 48, 64, 128, 256, 512],
    sizes: [0, 4, 8, 12, 16, 24, 32, 48, 64, 128, 256, 512],
    radii: [0, 4, 8, 24, 48],
    fonts: {
        body: 'basis-grotesque-pro, sans-serif',
        headingSerif: 'inferi, serif',
        headingSansSerif: 'basis-grotesque-pro, sans-serif',
        monospace: 'basis-grotesque-mono-regular, monospace'
    },
    fontSizes: [12, 13, 14, 15, 16, 18, 20, 24, 32, 40],
    fontWeights: {
        body: 400,
        headingSerif: 350,
        headingSansSerif: 400,
        bold: 700
    },
    lineHeights: {
        body: 1.4,
        button: '20px'
    },
    colors: {
        ...tiaColors,

        // colors required by theme-spec
        // https://theme-ui.com/theme-spec
        text: tiaColors.text,
        background: tiaColors.mainBackground,
        backgroundDisabled: tiaColors.inputBackgroundInactive,
        placeholder: tiaColors.inactiveText,
        placeholderDisabled: tiaColors.inactiveText,
        primary: tiaColors.raspberry,
        secondary: tiaColors.putty,
        accent: tiaColors.tiaPink,
        highlight: tiaColors.poppy,
        muted: tiaColors.cream
    },
    breakpoints: ['640px', '1080px'],
    shadows: {
        grey87: '0px 6px 14px -2px rgba(0, 0, 0, 0.15)'
    },
    layout: {
        container: {
            // Mobile max width is 480px plus 24px side padding: 528px
            // Tablet max width is 738px plus 48px side padding: 834px
            // Desktop max width is 1024px plus 48px side padding: 1120px
            maxWidth: ['528px', '834px', '1120px'],
            paddingX: [5, 7, 7]
        }
    },

    buttons: {
        unstyled: {
            'cursor': 'pointer',
            'borderRadius': 0,
            'fontFamily': 'inherit',
            'fontWeight': 'inherit',
            'color': 'inherit',
            'background': 'none',
            'p': 0,
            '&:focus': {
                outline: 'none'
            }
        },
        pink: {
            ...tiaButton,
            color: 'white',
            bg: 'pink'
        },
        blue: {
            ...tiaButton,
            color: 'white',
            bg: 'blue'
        },
        default: {
            ...tiaButton,
            color: 'white',
            bg: 'primaryButton'
        },
        disabled: {
            ...tiaButton,
            'color': 'inactiveText',
            'bg': 'inactiveButton',
            'cursor': 'initial',
            '&:hover, &:active': {
                filter: 'none'
            }
        },
        secondaryLight: {
            ...tiaButton,
            'color': 'lightSecondaryButton',
            'bg': 'transparent',
            'border': (t: Theme) =>
                `1px solid ${t.colors?.lightSecondaryButton}`,
            '&:hover': {
                // in theory this should work with t.colors.white
                // but it doesn't
                bg: (t: Theme) => `rgba(255, 255, 255, 0.2)`
            },
            '&:active': {
                bg: (t: Theme) => `rgba(0, 0, 0, 0.2)`,
                filter: 'none'
            }
        },
        secondaryDark: {
            ...tiaButton,
            'color': 'darkSecondaryButton',
            'bg': 'transparent',
            'border': (t: Theme) =>
                `1px solid ${t.colors?.darkSecondaryButton}`,
            '&:hover': {
                filter: 'none'
            },
            '&:active': {
                bg: (t: Theme) => `rgba(0, 0, 0, 0.2)`,
                filter: 'none'
            }
        },
        onDemandCare: {
            ...tiaButton,
            color: 'text',
            bg: 'onDemandCareButton'
        },
        icon: {
            ...tiaButton,
            color: 'white',
            bg: 'primaryButton',
            border: 'none',
            py: 0,
            px: 0,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        iconLight: {
            ...tiaButton,
            'color': 'primaryButton',
            'bg': 'white',
            'border': (t: Theme) => `1px solid ${t.colors?.inputOutline}`,
            'py': 0,
            'px': 0,
            'width': 40,
            'height': 40,
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            '&:hover': {
                border: (t: Theme) => `1px solid ${t.colors?.inputOutlineHover}`
            },
            '&:active': {
                border: (t: Theme) =>
                    `1px solid ${t.colors?.inputOutlineSelected}`
            }
        },
        filter: {
            ...tiaButton,
            'pr': 4,
            'pl': 4,
            'px': 4,
            'fontFamily': 'body',
            'fontWeight': 'body',
            'fontSize': 0,
            'bg': 'mainBackground',
            'color': 'text',
            'border': (t: Theme) =>
                `1px solid ${t.colors?.darkSecondaryButton}`,
            '&:active': {
                filter: (t: Theme) => `brightness(80%)`
            },
            '&:hover': {
                filter: (t: Theme) => `brightness(120%)`
            },
            '&:focus': {
                boxShadow: 'none',
                borderColor: 'text'
            }
        },
        selectedFilter: {
            ...tiaButton,
            'color': 'white',
            'bg': 'darkSecondaryButton',
            'fontFamily': 'inherit',
            'fontWeight': 'text',
            '&:hover': {
                filter: (t: Theme) => `brightness(120%)`
            },
            '&:active': {
                filter: (t: Theme) => `brightness(80%)`
            }
        }
    },

    cards: {
        primary: {
            p: 5,
            borderRadius: 2,
            color: 'text',
            border: (t: Theme) => `1px solid ${t.colors?.cardOutline}`,
            boxShadow: 'none',
            backgroundColor: 'white',
            textAlign: 'left'
        },
        info: {
            fontSize: 2,
            px: 5,
            py: 4,
            borderRadius: 2,
            border: 0,
            bg: 'secondaryBackground',
            textAlign: 'left'
        },
        condensed: {
            px: 5,
            py: 4,
            borderRadius: 2,
            border: 'cardOutline',
            boxShadow: 'none',
            backgroundColor: 'white',
            textAlign: 'left'
        }
    },

    text: {
        paragraph: {
            mb: 2,
            fontSize: 3,
            color: 'body',
            fontWeight: 'body',
            fontFamily: 'body',
            lineHeight: 'body'
        },
        button: {
            fontFamily: 'body',
            fontWeight: 'bold',
            fontSize: 3,
            lineHeight: 'button'
        },
        buttonSubtext: {
            fontSize: 0,
            fontWeight: 'body',
            fontFamily: 'body'
        },
        eyebrow: {
            fontFamily: 'monospace',
            fontSize: 0,
            lineHeight: '1.3em',
            letterSpacing: '0.1em',
            textTransform: 'uppercase'
        },
        supportingInfo: {
            fontFamily: 'body',
            fontWeight: 'body',
            fontSize: 2,
            lineHeight: '1.35em',
            color: 'supportText'
        },
        bigDesktop: {
            ...headings.h1,
            fontSize: 9
        },
        bigMobile: {
            ...headings.h1,
            fontSize: 8
        },
        ...headings
    },

    forms: {
        label: {
            fontSize: 2,
            fontWeight: 'body',
            fontFamily: 'body',
            mb: 1,
            color: 'text'
        },
        boldLabel: {
            fontWeight: 'bold',
            fontFamily: 'body',
            fontSize: 4,
            color: 'text'
        },
        input: {
            ...tiaInput
        },
        select: {
            ...tiaInput,
            '& + svg': {
                marginTop: '-24px',
                path: {
                    fill: 'inputOutline'
                }
            }
        },
        textarea: {
            ...tiaInput,
            py: 3,
            fontFamily: 'body',
            height: 7
        },
        slider: {
            'bg': 'inputBackgroundInactive',
            'height': 3,
            'mb': 4,
            'p': 0,
            'input:focus ~ &': {
                outline: 'none'
            },
            '&::-webkit-slider-thumb': {
                background: 'inputBackground',
                boxShadow: 'inputBackgroundInactive',
                height: 5,
                width: 5
            },
            '&::-moz-range-thumb': {
                background: 'inputBackground',
                boxShadow: 'inputBackgroundInactive',
                height: 5,
                width: 5
            },
            '&::-ms-thumb': {
                background: 'inputBackground',
                boxShadow: 'inputBackgroundInactive',
                height: 5,
                width: 5
            }
        },
        checkbox: {
            'mt': '-3px', // fix centering with default label mb
            'cursor': 'pointer',
            'color': 'darkIconDefault',
            'input:hover ~ &': {
                color: 'darkIconHover'
            },
            'input:focus ~ &': {
                color: 'darkIconHover',
                bg: 'rgba(0, 0, 0, 0)'
            },
            'input:checked ~ &': {
                color: 'darkIconHover'
            }
        },
        radio: {
            'mt': '-3px', // fix centering with default label mb
            'cursor': 'pointer',
            'color': 'inputOutline',
            'input:focus ~ &': {
                bg: 'inputBackground',
                border: 'oat'
            },
            'input:checked ~ &': {
                color: 'inputOutlineSelected'
            }
        }
    },

    // global styles for basics
    styles: {
        root: {
            fontFamily: 'body',
            lineHeight: 'body',
            fontWeight: 'body'
        },
        ...headings,
        p: {
            color: 'text',
            fontFamily: 'body',
            fontWeight: 'body',
            lineHeight: 'body',
            fontSize: 3,
            mb: 2
        },
        a: {
            'color': 'linkText',
            'textDecoration': 'underline',
            'fontSize': 2,
            '&:hover': {
                fontWeight: 'bold'
            }
        },
        pre: {
            fontFamily: 'monospace',
            overflowX: 'auto',
            code: {
                color: 'inherit'
            }
        },
        code: {
            fontFamily: 'monospace',
            fontSize: 'inherit'
        },
        table: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0
        },
        th: {
            textAlign: 'left',
            borderBottomStyle: 'solid'
        },
        td: {
            textAlign: 'left',
            borderBottomStyle: 'solid'
        },
        img: {
            maxWidth: '100%'
        }
    }
}

export type DefaultTheme = typeof defaultTheme & Theme

export const GlobalCSS = `
    @font-face {
        font-family: 'basis-grotesque-pro';
        src: url('https://assets.asktia.com/fonts/basis-grotesque-regular-pro.eot');
        src: url('https://assets.asktia.com/fonts/basis-grotesque-regular-pro.eot?#iefix') format('embedded-opentype'),
            url('https://assets.asktia.com/fonts/basis-grotesque-regular-pro.woff') format('woff'),
            url('https://assets.asktia.com/fonts/basis-grotesque-regular-pro.ttf') format('truetype');
        font-weight: normal;
        font-style: normal;
    }

    @font-face {
        font-family: 'inferi';
        src: url('https://assets.asktia.com/fonts/Inferi-Normal.eot');
        src: url('https://assets.asktia.com/fonts/Inferi-Normal.eot?#iefix') format('embedded-opentype'),
            url('https://assets.asktia.com/fonts/Inferi-Normal.woff2') format('woff2'),
            url('https://assets.asktia.com/fonts/Inferi-Normal.woff') format('woff'),
            url('https://assets.asktia.com/fonts/Inferi-Normal.ttf') format('truetype');
        font-weight: normal;
        font-style: normal;
    }

    @font-face {
        font-family: 'inferi';
        src: url('https://assets.asktia.com/fonts/Inferi-NormalItalic.eot');
        src: url('https://assets.asktia.com/fonts/Inferi-NormalItalic.eot?#iefix') format('embedded-opentype'),
            url('https://assets.asktia.com/fonts/Inferi-NormalItalic.woff2') format('woff2'),
            url('https://assets.asktia.com/fonts/Inferi-NormalItalic.woff') format('woff'),
            url('https://assets.asktia.com/fonts/Inferi-NormalItalic.ttf') format('truetype');
        font-style: italic;
    }

    @font-face {
        font-family: 'basis-grotesque-mono-regular';
        src: url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.eot');
        src: url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.eot?#iefix') format('embedded-opentype'),
            url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.woff2') format('woff2'),
            url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.woff') format('woff'),
            url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.ttf') format('truetype');
        font-weight: normal;
        font-style: normal;
    }
`

export const DeprecatedGlobalCSS = `
@font-face {
    font-family: 'basis-grotesque-bold';
    src: url('https://assets.asktia.com/fonts/basis-grotesque-bold.eot');
    src: url('https://assets.asktia.com/fonts/basis-grotesque-bold.eot?#iefix') format('embedded-opentype'),
        url('https://assets.asktia.com/fonts/basis-grotesque-bold.woff2') format('woff2'),
        url('https://assets.asktia.com/fonts/basis-grotesque-bold.woff') format('woff'),
        url('https://assets.asktia.com/fonts/basis-grotesque-bold.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
}
@font-face {
    font-family: 'basis-grotesque-regular';
    src: url('https://assets.asktia.com/fonts/basis-grotesque-regular.eot');
    src: url('https://assets.asktia.com/fonts/basis-grotesque-regular.eot?#iefix') format('embedded-opentype'),
        url('https://assets.asktia.com/fonts/basis-grotesque-regular.woff2') format('woff2'),
        url('https://assets.asktia.com/fonts/basis-grotesque-regular.woff') format('woff'),
        url('https://assets.asktia.com/fonts/basis-grotesque-regular.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
}
@font-face {
    font-family: 'basis-grotesque-mono-regular';
    src: url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.eot');
    src: url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.eot?#iefix') format('embedded-opentype'),
        url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.woff2') format('woff2'),
        url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.woff') format('woff'),
        url('https://assets.asktia.com/fonts/basis-grotesque-mono-regular.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
}
@font-face {
    font-family: 'karla-regular';
    src: url('https://assets.asktia.com/fonts/Karla-Regular.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
}`
